import { Component, Input, OnInit } from "@angular/core";
import { Network } from "@modules/+new/payments/features/crypto-deposit/domain/entities/network.model";
import { PaymentsCryptocoin } from "@modules/+new/payments/shared-payments/entities/payments-cryptocoin.model";
import { ModalService } from "@core-services/modal.service";
import { SendingModalRemoteService } from "@modules/main/components/step-summary/sending-modal/services/sending-modal-remote.service";
import { Conversion, CryptoWallet, Fiat, Price } from "@models";
import { BehaviorSubject, combineLatest, EMPTY, Observable } from "rxjs";
import { AccountService } from "@modules/account/services/account.service";
import { CryptoWalletService } from "@main-services/wallet/crypto_wallet.service";
import { SendAmountService } from "@modules/main/components/step-summary/sending-modal/send-amount/send-amount.service";
import { Fee } from "@modules/main/components/step-summary/sending-modal/entities/fee.model";
import { Big } from "big.js";
import { WalletBalanceApiModel } from "@modules/+new/core/wallets-balances/domain/entities/wallet-balance.api.model";
import { SendTransaction } from "@models/transaction";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { map } from "rxjs/operators";
import { config } from "@config";
import { SendMultichainSummaryComponent } from "@modules/main/components/step-summary/sending-modal/send-multichain-summary/send-multichain-summary.component";
import { SendTrackingFacade } from "@modules/main/components/step-summary/sending-modal/services/send-tracking.facade";
import { CryptocoinsState } from "@modules/+new/payments/shared-payments/services/cryptocoins-state.service";

enum CurrencyType {
	Fiat = "fiat",
	Cryptocoin = "cryptocoin"
}

@Component({
	selector: "send-amount",
	templateUrl: "./send-amount.component.html",
	styleUrls: ["./send-amount.component.scss"],
	providers: [SendingModalRemoteService, SendAmountService],
	standalone: false
})
export class SendAmountComponent implements OnInit {
	@Input() selectedNetwork: Network = null;
	@Input() selectedCryptocoin: PaymentsCryptocoin = null;
	@Input() sendTransaction: SendTransaction = null;
	@Input() fee: Fee = null;
	@Input() contactName: string = "";
	@Input() selectedHost?: string = "";
	@Input() legalEntityIdentifier?: string = "";
	public price$: Observable<Price> = EMPTY;
	public cryptoPrecision$: Observable<Number> = EMPTY;
	public fiat: Fiat = this.accountService.getDefaultFiat();
	public selectedCurrency$: Observable<CurrencyType> = EMPTY;
	public cryptoWallet: CryptoWallet = null;
	public cryptoWalletBalanceStatus$: Observable<WalletBalanceApiModel> = EMPTY;
	public inputAmount: string = "0";
	public totalAmountInCrypto: string = "0";
	public totalAmountInCryptoWithoutFees: string = "0";
	public amountWithoutFeesInFiat: string = "0";
	public amountData$: Observable<{
		price: Price;
		balanceStatus: WalletBalanceApiModel;
		selectedCurrency: CurrencyType;
		precision: Number;
		cryptoPrecision: Number;
	}> = EMPTY;
	public isMaxSet: boolean = false;

	private selectedCurrencySubject: BehaviorSubject<CurrencyType> = new BehaviorSubject<CurrencyType>(
		CurrencyType.Fiat
	);

	constructor(
		private readonly modalService: ModalService,
		private readonly sendingModalRemoteService: SendingModalRemoteService,
		private readonly accountService: AccountService,
		private readonly walletService: CryptoWalletService,
		private readonly sendAmountService: SendAmountService,
		private readonly infoService: InfoService,
		private readonly translationService: TranslationService,
		private readonly trackingFacade: SendTrackingFacade,
		private readonly cryptocoinsState: CryptocoinsState
	) {}

	ngOnInit(): void {
		this.trackingFacade.trackAmountScreenViewed();
		this.selectedCurrency$ = this.selectedCurrencySubject.asObservable();
		this.cryptoWallet = this.walletService.getItemById(this.sendTransaction?.getWalletId());
		this.price$ = this.sendingModalRemoteService.fetchPriceLimits(
			"external",
			this.sendTransaction?.getWalletId(),
			this.selectedNetwork.coin_network_id
		);
		this.cryptoWalletBalanceStatus$ = this.sendAmountService.getBalances$(this.sendTransaction?.getWalletId());
		this.cryptoPrecision$ = this.cryptocoinsState.cryptocoins.pipe(
			map((list) =>
				list.find((item) => item.cryptocoin_id.toString() === this.selectedCryptocoin.cryptocoin_id.toString())
			),
			map((crypto) => {
				const network = crypto?.networks.find(
					(network) => network.coin_network_id === this.selectedNetwork.coin_network_id
				);
				return network?.precision_for_tx ?? 8;
			})
		);
		//combine them and filter
		this.amountData$ = combineLatest([
			this.price$,
			this.cryptoWalletBalanceStatus$,
			this.cryptoPrecision$,
			this.selectedCurrency$
		]).pipe(
			map(([price, balanceStatus, cryptoPrecision, selectedCurrency]) => {
				return {
					price: price,
					balanceStatus: balanceStatus,
					selectedCurrency: selectedCurrency,
					cryptoPrecision: cryptoPrecision,
					precision: selectedCurrency === CurrencyType.Cryptocoin ? cryptoPrecision : 2
				};
			})
		);
	}

	public onBackButtonClick(): void {
		this.modalService.pop();
	}

	public onCancelButtonClick(): void {
		this.modalService.popAll();
	}

	public getTradableAmountTitleConversion(price: Price, precision: number): Conversion {
		const adjustedConversion = price.getMaxAmountExternal();
		const truncatedAmount = this.truncateAmount(adjustedConversion.tradableAmount(), precision);
		adjustedConversion.setTradableAmount(truncatedAmount);
		return adjustedConversion;
	}

	public setSendMaxAmount(price: Price, precision: number, selectedCurrency: CurrencyType): void {
		this.isMaxSet = true;
		if (selectedCurrency === CurrencyType.Fiat) {
			this.changeCurrency(price, precision, selectedCurrency);
		}
		this.inputAmount = this.truncateAmount(price.getMaxAmountExternal().tradableAmount(), precision);
	}

	private truncateAmount(amount: string, precision: number): string {
		const array = amount.split(".");
		if (array.length === 1 || precision === 0) {
			return array[0];
		}
		if (array[1].length > precision) {
			return array[0] + "." + array[1].substring(0, precision);
		}
		return array[0] + "." + array[1];
	}

	public gotoSummary(price: Price, precision: number, cryptoPrecision: number): void {
		const amountTotal = new Big(
			this.isMaxSet
				? this.truncateAmount(price.getMaxAmountExternal().tradableAmount(), precision)
				: this.totalAmountInCryptoWithoutFees
		);
		if (amountTotal.gt(new Big(+price.getMaxAmountExternal().tradableAmount()))) {
			this.infoService.setError(this.translationService.translate("send_amount_larger_than_wallet_balance"));
			return;
		}
		if (amountTotal.lt(new Big(+price.getMinAmountExternal().tradableAmount()))) {
			this.infoService.setError(this.translationService.translate("transaction_amount_low"));
			return;
		}
		this.totalAmountInCrypto = this.truncateAmount(this.totalAmountInCrypto, cryptoPrecision);
		this.sendTransaction.setAmountCoin(this.truncateAmount(amountTotal.toString(), cryptoPrecision));
		this.sendTransaction.setAmountFiat(this.amountWithoutFeesInFiat);
		this.trackingFacade.trackAmountScreenNextClicked();
		this.modalService
			.create(SendMultichainSummaryComponent, {
				sendTransaction: this.sendTransaction,
				fee: this.fee,
				selectedCryptocoin: this.selectedCryptocoin,
				selectedNetwork: this.selectedNetwork,
				contactName: this.contactName,
				selectedHost: this.selectedHost,
				legalEntityIdentifier: this.legalEntityIdentifier
			})
			.present();
		this.isMaxSet = false;
	}

	public amountChange(
		value: string,
		price: Price,
		precision: number,
		selectedCurrency: CurrencyType,
		cryptoPrecision: number
	): void {
		value = this.truncateAmount(value, precision);
		if (value === "" || price === null || price === undefined || +value === 0 || value === "0") {
			this.totalAmountInCrypto = "0";
			this.amountWithoutFeesInFiat = "0";
		} else {
			this.calculateCorrectAmount(value, price, precision, selectedCurrency, cryptoPrecision);
		}
	}

	private calculateCorrectAmount(
		value: string,
		price: Price,
		precision: number,
		selectedCurrency: CurrencyType,
		cryptoPrecision: number
	): void {
		const cryptoPrice = new Big(price.getPrice().fiatAmount());
		const decimalValue = new Big(value);
		const decimalFee = new Big(this.fee.fee);
		if (selectedCurrency === CurrencyType.Cryptocoin) {
			this.amountWithoutFeesInFiat = this.truncateAmount(decimalValue.times(cryptoPrice).toString(), 2);
			this.totalAmountInCrypto = decimalFee.plus(decimalValue).toString();
			this.totalAmountInCryptoWithoutFees = decimalValue.toString();
		} else {
			this.amountWithoutFeesInFiat = this.truncateAmount(decimalValue.toString(), 2);
			this.totalAmountInCrypto = this.truncateAmount(
				Number(decimalFee.plus(decimalValue.div(cryptoPrice)))
					.toFixed(8)
					.toString(),
				cryptoPrecision
			);
			this.totalAmountInCryptoWithoutFees = this.truncateAmount(
				Number(decimalValue.div(cryptoPrice).toFixed(8)).toString(),
				cryptoPrecision
			);
		}
	}

	public changeCurrency(price: Price, precision: number, selectedCurrency: CurrencyType): void {
		this.isMaxSet = false;
		this.trackingFacade.trackAmountScreenCurrencyTypeClicked();
		this.selectedCurrencySubject.next(
			selectedCurrency === CurrencyType.Fiat ? CurrencyType.Cryptocoin : CurrencyType.Fiat
		);
		if (selectedCurrency === CurrencyType.Cryptocoin) {
			//means it was crypto before so we need to turn crypto into fiat
			this.inputAmount = this.truncateAmount(
				new Big(this.inputAmount)
					.times(new Big(this.truncateAmount(price.getPrice().fiatAmount(), 2)))
					.toString(),
				2
			);
		} else {
			//means it was fiat before so we need to turn fiat into crypto
			this.inputAmount = this.truncateAmount(
				new Big(this.inputAmount)
					.div(new Big(this.truncateAmount(price.getPrice().fiatAmount(), precision)))
					.toString(),
				precision
			);
		}
	}

	public clickedFeeTooltip(): string {
		return this.translationService.getHdLink() + config.externalPaths["send"]["tooltip_fee"];
	}
}
